import { DateTime } from "luxon";

import { IBFHistoryLog, IBF_HISTORY_LOG } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_HISTORY_LOGS";
import { IBFAlarmLog, IBF_ALARM_LOG } from "@/lib/drivers/insulet/IBFRecords/IBF_ALARM_LOGS";
import {
  IBFBatchRecordParsingDefinition,
  IBFParsedRecord,
  IBFRecordParsingDefinition,
} from "@/lib/drivers/insulet/IBFRecords/RecordParser";

export type IBFLogRecordHeader = {
  logId: number;
  logIndex: number;
  recordSize: number;
  date: DateTime;
};

export type IBFLogRecordHistory<T> = IBFLogRecordHeader & {
  type: "HISTORY";
  history: IBFHistoryLog<T>;
};

export type IBFLogRecordAlarm = IBFLogRecordHeader & {
  type: "PUMP_ALARM";
  alarm: IBFAlarmLog;
};

export type IBFLogEmptyRecord = IBFLogRecordHeader & {
  type: "EMPTY";
};

export type IBFLogRecord = IBFLogRecordHistory<unknown> | IBFLogRecordAlarm | IBFLogEmptyRecord;

export type ParsedIBFLogRecordList = {
  logs: IBFLogRecord[];
  ignored: number;
  start?: DateTime;
  end?: DateTime;
};

const LOG_TYPES = {
  HISTORY: { value: 0x03, name: "HISTORY" },
  PUMP_ALARM: { value: 0x05, name: "PUMP_ALARM" },
  DELETED: { mask: 0x80000000, name: "DELETED" },
  // this is an impossible value for a 1-byte LOG_TYPE
  IGNORE: { value: 0x100, name: "IGNORED by driver" },
};

const IBF_LOG_RECORD = {
  value: 0,
  name: "IBF_LOG_RECORD",
  size: 18,
  parse: (record, info) => {
    const logHeader = IBF_LOG_RECORD_HEADER.parse(record);

    if (logHeader.logIndex === -1 || logHeader.errorCode !== 0) {
      return {
        ...logHeader,
        type: "EMPTY",
      };
    }
    if (logHeader.logId === LOG_TYPES.HISTORY.value) {
      const history = IBF_HISTORY_LOG(record, info);
      if (history) {
        return {
          ...logHeader,
          type: "HISTORY",
          history,
        };
      }
    } else if (logHeader.logId === LOG_TYPES.PUMP_ALARM.value) {
      const alarmLog = IBF_ALARM_LOG(record);
      if (alarmLog) {
        return {
          ...logHeader,
          type: "PUMP_ALARM",
          alarm: alarmLog,
        };
      }
    }
    return {
      ...logHeader,
      type: "EMPTY",
    };
  },
} satisfies IBFRecordParsingDefinition<IBFLogRecord>;

export const IBF_LOG_RECORD_HEADER = {
  value: 0,
  name: "IBF_LOG_RECORD_HEADER",
  size: 18,
  parse: record => {
    const logId = record.nextUInt8();
    const logIndex = record.nextInt32BE();
    const recordSize = record.nextUInt16BE();
    const errorCode = record.nextUInt16BE();
    const date = record.nextDateTime();

    return {
      logId,
      logIndex,
      recordSize,
      date,
      errorCode,
    };
  },
} satisfies IBFRecordParsingDefinition<IBFLogRecordHeader>;

export const IBF_LOG_RECORDS = {
  value: 8,
  name: "IBF_LOG_RECORDS",
  recordsCount: "all",
  definition: IBF_LOG_RECORD,
  parse: logs => {
    const filteredLogs = logs.filter(log => log.data !== undefined) as IBFParsedRecord<IBFLogRecord>[];

    const firstLog = filteredLogs.length > 0 ? filteredLogs[filteredLogs.length - 1] : undefined;
    const lastLog = filteredLogs.length > 0 ? filteredLogs[0] : undefined;

    const result = {
      logs: [] as IBFLogRecord[],
      ignored: 0,
      start: firstLog?.data?.date,
      end: lastLog?.data?.date,
    };
    for (const log of filteredLogs) {
      result.logs.push(log.data);
    }

    return result;
  },
} satisfies IBFBatchRecordParsingDefinition<IBFLogRecord, ParsedIBFLogRecordList>;
